import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import MatrixRain from '@/components/MatrixRain';
import LocalMatrixToggle, { useMatrixMode } from '@/components/LocalMatrixToggle';
import VersionBadge from '@/components/VersionBadge';
import BuzzPassport from '@/components/BuzzPassport';
import SocialFeed from '@/components/SocialFeed';
import { AuthContext, API } from '@/App';
import { Zap, Trophy, Calendar as CalendarIcon, AlertTriangle, Shield, User, MapPin, Bell, Clock, X, Send, Copy } from 'lucide-react';

const GREEN = '#00FF41';
const GREEN_GLOW = 'rgba(0,255,65,0.8)';

const CATEGORIES = [
  { emoji: '🎮', label: 'GAMING' },
  { emoji: '☕', label: 'COFFEE' },
  { emoji: '🎳', label: 'BOWLING' },
  { emoji: '🎬', label: 'CINEMA' },
  { emoji: '🎤', label: 'KARAOKE' },
  { emoji: '🏓', label: 'ARCADE/REC' },
];

const CATEGORY_CARDS = [
  { emoji: '🎮', title: 'GAME LABS', category: 'GAMING' },
  { emoji: '☕', title: 'COFFEE SPOTS', category: 'COFFEE' },
  { emoji: '🎳', title: 'BOWLING', category: 'BOWLING' },
  { emoji: '🎬', title: 'CINEMA', category: 'CINEMA' },
  { emoji: '🎤', title: 'KARAOKE', category: 'KARAOKE' },
  { emoji: '🏓', title: 'ARCADE/REC', category: 'ARCADE/REC' },
];

const glowText = { color: GREEN, textShadow: `0 0 8px ${GREEN_GLOW}` };
const monoFont = { fontFamily: 'JetBrains Mono, ui-monospace, monospace' };

export default function YoungBuzzDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  const mode = useMatrixMode('nb-yb-mode', 'neon');
  const isMatrix = mode === 'matrix';

  const [tribeModalOpen, setTribeModalOpen] = useState(false);
  const [myTribes, setMyTribes] = useState([]);
  const [activeTribe, setActiveTribe] = useState(null);
  const [tribeName, setTribeName] = useState('');
  const [tribeCategory, setTribeCategory] = useState('GAMING');
  const [tribeDescription, setTribeDescription] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [pingMessage, setPingMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [publicStats, setPublicStats] = useState({ active_tribes: 0, total_tribes: 0 });

  useEffect(() => {
    fetchPublicStats();
    if (user) fetchMyTribes();
  }, [user]);

  const fetchPublicStats = async () => {
    try {
      const res = await axios.get(`${API}/tribes/public/stats`);
      setPublicStats(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchMyTribes = async () => {
    try {
      const res = await axios.get(`${API}/tribes/my-tribes`);
      setMyTribes(res.data.tribes || []);
      if ((res.data.tribes || []).length && !activeTribe) {
        setActiveTribe(res.data.tribes[0]);
      }
    } catch (e) { /* not logged in */ }
  };

  const requireAuth = (msg = 'Log in to use this feature') => {
    if (!user) { toast.error(msg); navigate('/auth'); return false; }
    return true;
  };

  const createTribe = async () => {
    if (!requireAuth('Log in to start a tribe')) return;
    if (!tribeName.trim()) { toast.error('Give your tribe a name'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tribes/create`, {
        name: tribeName.trim(),
        category: tribeCategory,
        description: tribeDescription.trim() || null,
      });
      toast.success(`Tribe "${res.data.name}" created! Share code: ${res.data.code}`);
      setMyTribes((prev) => [res.data, ...prev]);
      setActiveTribe(res.data);
      setTribeName(''); setTribeDescription('');
      setTribeModalOpen(false);
      fetchPublicStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create tribe');
    } finally { setLoading(false); }
  };

  const joinTribe = async () => {
    if (!requireAuth('Log in to join a tribe')) return;
    if (!joinCode.trim()) { toast.error('Enter a tribe code'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tribes/join`, { code: joinCode.trim().toUpperCase() });
      toast.success(`Joined "${res.data.name}"`);
      setActiveTribe(res.data);
      setJoinCode('');
      fetchMyTribes();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to join');
    } finally { setLoading(false); }
  };

  const leaveTribe = async (tribeId) => {
    if (!window.confirm('Leave this tribe?')) return;
    try {
      await axios.post(`${API}/tribes/${tribeId}/leave`);
      toast.success('Left tribe');
      setActiveTribe(null);
      fetchMyTribes();
    } catch (e) {
      toast.error('Failed to leave');
    }
  };

  const pingTribe = async () => {
    if (!activeTribe) return;
    if (!pingMessage.trim()) { toast.error('Type a message'); return; }
    try {
      await axios.post(`${API}/tribes/${activeTribe.id}/ping`, { message: pingMessage.trim() });
      toast.success('Ping sent to tribe');
      setPingMessage('');
    } catch (e) {
      toast.error('Ping failed');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code ${code} copied`);
  };

  const openCategory = (cat) => {
    navigate(`/home?category=${encodeURIComponent(cat)}`);
  };

  // Feature buttons wired to real destinations
  const FEATURES = [
    { emoji: '🎮', label: 'Game Matchmaking', onClick: () => navigate('/matchmaking') },
    { emoji: '👥', label: 'Group Finder', onClick: () => setTribeModalOpen(true) },
    { emoji: '📊', label: 'Leaderboards', onClick: () => navigate('/leaderboard') },
    { emoji: '🎟️', label: 'Event Tickets', onClick: () => navigate('/singles-night') },
    { emoji: '🛡️', label: 'Safe Social', onClick: () => navigate('/feed') },
    { emoji: '🗺️', label: 'Venue Maps', onClick: () => navigate('/home') },
    { emoji: '⭐', label: 'Rewards Points', onClick: () => navigate('/profile') },
    { emoji: '👨‍👩‍👧‍👦', label: 'Family Mode', onClick: () => navigate('/profile?section=family') },
  ];

  const SAFETY = [
    { Icon: User, label: 'AGE VERIFICATION', status: 'ACTIVE', onClick: () => navigate('/profile?section=age') },
    { Icon: MapPin, label: 'PARENT TRACKING', status: 'ENABLED', onClick: () => navigate('/profile?section=family') },
    { Icon: AlertTriangle, label: 'EMERGENCY ALERTS', status: 'STANDBY', onClick: () => navigate('/profile?section=emergency') },
    { Icon: Clock, label: 'CURFEW MONITOR', status: '30 MIN SET', onClick: () => navigate('/profile?section=curfew') },
  ];

  return (
    <div className="relative min-h-screen bg-black text-white" style={monoFont}>
      {isMatrix && <MatrixRain />}
      <LocalMatrixToggle storageKey="nb-yb-mode" defaultMode="neon" />
      <VersionBadge variant="youngbuzz-v1" />

      <div className="relative z-10 max-w-5xl mx-auto p-4 sm:p-6 space-y-5">
        {/* OS Header bar */}
        <div className="flex items-center justify-between border-b border-emerald-500/30 pb-2">
          <span className="text-[10px] sm:text-xs tracking-widest" style={glowText}>NIGHTBUZZ OS v1.0.0</span>
          <span className="text-xs sm:text-sm font-black tracking-widest" style={glowText}>ALL-AGES OPERATOR HUB</span>
          <span className="text-[10px] sm:text-xs tracking-widest flex items-center gap-1" style={glowText}>
            STATUS: <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </span>
        </div>

        {/* Hero */}
        <div className="text-center py-2">
          <h1
            data-testid="yb-hero-title"
            className="font-black tracking-tight"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', color: GREEN, textShadow: `0 0 20px ${GREEN_GLOW}` }}
          >
            NIGHTBUZZ
          </h1>
          <p
            className="font-black tracking-widest -mt-1"
            style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', color: GREEN, textShadow: `0 0 15px ${GREEN_GLOW}` }}
          >
            BUZZ
          </p>
          <p className="text-xs sm:text-sm tracking-wider mt-1" style={glowText}>
            THE OPERATING SYSTEM FOR ALL-AGES ENTERTAINMENT
          </p>
          <div className="flex justify-center flex-wrap gap-2 sm:gap-4 mt-3 border border-emerald-500/30 rounded-lg px-3 py-2 max-w-xl mx-auto">
            {CATEGORIES.map((c) => (
              <button
                key={c.label}
                data-testid={`yb-cat-pill-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                onClick={() => openCategory(c.label)}
                className="text-[10px] sm:text-xs flex items-center gap-1 hover:scale-110 transition"
                style={glowText}
              >
                <span className="text-base">{c.emoji}</span> {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* START TRIBE + JOIN buttons */}
        <div className="flex justify-center flex-wrap gap-3">
          <button
            data-testid="start-tribe-btn"
            onClick={() => setTribeModalOpen(true)}
            className="flex items-center gap-2 px-8 py-4 rounded-full font-black tracking-widest text-white text-sm sm:text-base transition hover:scale-105"
            style={{
              background: 'linear-gradient(90deg, #00FF41, #00CC00)',
              boxShadow: '0 0 30px rgba(0,255,65,0.7)',
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            <Zap className="h-5 w-5 text-yellow-300" />
            {myTribes.length ? `TRIBE ACTIVE (${myTribes.length})` : 'START TRIBE'}
          </button>
          <div className="flex items-center gap-2">
            <Input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="JOIN CODE"
              maxLength={12}
              className="w-32 bg-black/60 border-emerald-500/40 text-emerald-200 uppercase text-center font-mono tracking-widest"
              data-testid="tribe-join-code-input"
            />
            <Button
              onClick={joinTribe}
              disabled={loading}
              variant="outline"
              className="border-emerald-500/60 text-emerald-300 hover:bg-emerald-900/30"
              data-testid="tribe-join-btn"
            >
              JOIN
            </Button>
          </div>
        </div>

        {/* Active Tribe Panel */}
        {activeTribe && (
          <Card className="bg-black/70 border-2 border-emerald-500/60 p-4 space-y-3" data-testid="active-tribe-panel">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-black text-emerald-300 tracking-widest">{activeTribe.name}</p>
                <p className="text-xs text-emerald-200/70">{activeTribe.category} · {activeTribe.member_count} member{activeTribe.member_count === 1 ? '' : 's'}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyCode(activeTribe.code)}
                  className="flex items-center gap-1 px-3 py-1 rounded border border-emerald-500/60 bg-black/60 text-emerald-300 text-xs font-mono hover:bg-emerald-900/30"
                  data-testid="tribe-copy-code-btn"
                >
                  <Copy className="w-3 h-3" /> {activeTribe.code}
                </button>
                <button
                  onClick={() => leaveTribe(activeTribe.id)}
                  className="p-1 rounded text-red-400 hover:bg-red-900/30"
                  title="Leave tribe"
                  data-testid="tribe-leave-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Input
                value={pingMessage}
                onChange={(e) => setPingMessage(e.target.value)}
                placeholder="Send a ping to your tribe…"
                className="bg-black/60 border-emerald-500/40 text-emerald-200"
                data-testid="tribe-ping-input"
              />
              <Button
                onClick={pingTribe}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="tribe-ping-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {myTribes.length > 1 && (
              <div className="flex gap-2 flex-wrap pt-2 border-t border-emerald-500/20">
                {myTribes.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTribe(t)}
                    className={`text-[10px] px-2 py-1 rounded border transition ${t.id === activeTribe.id ? 'border-emerald-400 bg-emerald-900/40 text-white' : 'border-emerald-500/30 text-emerald-300 hover:bg-emerald-900/20'}`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Category cards — clickable */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {CATEGORY_CARDS.map((c) => (
            <button
              key={c.title}
              data-testid={`yb-cat-${c.title.toLowerCase().replace(/[^a-z]/g, '-')}`}
              onClick={() => openCategory(c.category)}
              className="text-left"
            >
              <Card className="bg-black/60 border border-emerald-500/30 p-4 text-center hover:border-emerald-400/70 hover:scale-105 transition cursor-pointer">
                <div className="text-3xl mb-1">{c.emoji}</div>
                <p className="text-xs font-bold" style={glowText}>{c.title}</p>
                <p className="text-[10px] mt-1 text-emerald-300/80">EXPLORE →</p>
              </Card>
            </button>
          ))}
        </div>

        {/* Live network stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="bg-black/60 border border-emerald-500/30 p-3">
            <p className="text-[10px] font-mono tracking-widest" style={glowText}>ACTIVE TRIBES</p>
            <p className="text-2xl sm:text-3xl font-black text-white leading-tight" data-testid="stat-active-tribes">{publicStats.active_tribes}</p>
            <p className="text-[10px] text-zinc-400">Live squads right now</p>
          </Card>
          <Card className="bg-black/60 border border-emerald-500/30 p-3">
            <p className="text-[10px] font-mono tracking-widest" style={glowText}>YOUR TRIBES</p>
            <p className="text-2xl sm:text-3xl font-black text-white leading-tight" data-testid="stat-my-tribes">{myTribes.length}</p>
            <p className="text-[10px] text-zinc-400">Groups you&apos;re part of</p>
          </Card>
          <Card className="bg-black/60 border border-emerald-500/30 p-3">
            <p className="text-[10px] font-mono tracking-widest" style={glowText}>TOTAL TRIBES</p>
            <p className="text-2xl sm:text-3xl font-black text-white leading-tight" data-testid="stat-total-tribes">{publicStats.total_tribes}</p>
            <p className="text-[10px] text-zinc-400">All-time squads formed</p>
          </Card>
        </div>

        {/* Safety & Compliance — clickable */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <Shield className="h-4 w-4" /> SAFETY & COMPLIANCE
          </p>
          <div className="grid grid-cols-2 gap-2">
            {SAFETY.map(({ Icon, label, status, onClick }) => (
              <button
                key={label}
                onClick={onClick}
                data-testid={`yb-safety-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="border border-emerald-500/20 rounded p-2 bg-black/40 flex items-center gap-2 hover:border-emerald-400 hover:bg-emerald-900/20 transition text-left"
              >
                <Icon className="h-4 w-4 text-emerald-300" />
                <div>
                  <p className="text-[10px] font-mono" style={glowText}>{label}</p>
                  <p className="text-[10px] text-white">{status}</p>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* YounG BuzZ Feature buttons — all wired */}
        <Card className="bg-black/60 border border-emerald-500/30 p-4">
          <p className="text-xs font-mono tracking-widest flex items-center gap-2 mb-3" style={glowText}>
            <Zap className="h-4 w-4 text-yellow-300" /> YOUNG BUZZ FEATURES
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FEATURES.map((f) => (
              <button
                key={f.label}
                onClick={f.onClick}
                data-testid={`yb-feature-${f.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                className="flex items-center gap-2 px-3 py-3 rounded border border-emerald-500/30 bg-black/40 text-xs text-emerald-100 hover:bg-emerald-900/20 hover:border-emerald-400 hover:scale-105 transition"
              >
                <span className="text-base">{f.emoji}</span> {f.label}
              </button>
            ))}
          </div>
        </Card>

        {/* Quick links — top events & leaderboard */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/singles-night')}
            className="text-left"
            data-testid="yb-quick-events"
          >
            <Card className="bg-black/60 border border-emerald-500/30 p-4 hover:border-emerald-400 hover:bg-emerald-900/10 transition cursor-pointer">
              <p className="text-xs font-mono tracking-widest flex items-center gap-2" style={glowText}>
                <Bell className="h-4 w-4 text-red-400" /> UPCOMING EVENTS
              </p>
              <p className="text-sm text-emerald-100 mt-1">Browse tournaments, family nights, meetups →</p>
            </Card>
          </button>
          <button
            onClick={() => navigate('/leaderboard')}
            className="text-left"
            data-testid="yb-quick-leaderboard"
          >
            <Card className="bg-black/60 border border-emerald-500/30 p-4 hover:border-emerald-400 hover:bg-emerald-900/10 transition cursor-pointer">
              <p className="text-xs font-mono tracking-widest flex items-center gap-2" style={glowText}>
                <Trophy className="h-4 w-4" /> LEADERBOARDS
              </p>
              <p className="text-sm text-emerald-100 mt-1">Top venues + top tribes this week →</p>
            </Card>
          </button>
        </div>

        {/* Footer */}
        <div className="text-center pt-4 border-t border-emerald-500/20">
          <p className="text-xs sm:text-sm tracking-widest italic" style={glowText}>
            &ldquo;WHAT IF I TOLD YOU, FUN HAS NO AGE LIMIT.&rdquo;
          </p>
          <p className="text-[10px] font-mono mt-1 text-emerald-300/60">— YOUNG BUZZ</p>
          <p className="text-[9px] font-mono mt-2 text-emerald-200/40">POWERED BY NIGHTBUZZ OS V1.0.0 · ALL-AGES SAFE · UPTIME: 98%</p>
        </div>

        {/* Buzz Passport */}
        <BuzzPassport />

        {/* Instagram-style Social Feed */}
        <SocialFeed
          feedKey="youngbuzz"
          tokenKey="token"
          title="🎮 YounG BuzZ Social Feed"
          accentColor="#00FF41"
          glowColor="#9D00FF"
          showLike={true}
        />
      </div>

      {/* Create Tribe Modal */}
      <Dialog open={tribeModalOpen} onOpenChange={setTribeModalOpen}>
        <DialogContent className="bg-black border border-emerald-500/60 text-white" data-testid="create-tribe-modal">
          <DialogHeader>
            <DialogTitle style={glowText} className="tracking-widest">START A NEW TRIBE</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono tracking-widest" style={glowText}>TRIBE NAME</label>
              <Input
                value={tribeName}
                onChange={(e) => setTribeName(e.target.value)}
                placeholder="e.g. Squad Fortnite Fridays"
                className="bg-black/60 border-emerald-500/40 text-white"
                data-testid="tribe-name-input"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest" style={glowText}>CATEGORY</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.label}
                    onClick={() => setTribeCategory(c.label)}
                    className={`text-xs py-2 rounded border transition ${tribeCategory === c.label ? 'border-emerald-400 bg-emerald-900/40 text-white' : 'border-emerald-500/30 text-emerald-300 hover:bg-emerald-900/20'}`}
                    data-testid={`tribe-cat-${c.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                  >
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-widest" style={glowText}>WHAT&apos;S THE VIBE? (optional)</label>
              <Textarea
                value={tribeDescription}
                onChange={(e) => setTribeDescription(e.target.value)}
                placeholder="Meeting at Level Up Lounge 7pm, bring your controllers…"
                className="bg-black/60 border-emerald-500/40 text-white"
                data-testid="tribe-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTribeModalOpen(false)}>Cancel</Button>
            <Button
              onClick={createTribe}
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="tribe-create-submit-btn"
            >
              {loading ? 'Creating…' : 'CREATE TRIBE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
